import React from 'react';

/*
 * Tabs and filters, deliberately different weights.
 *
 * Tabs switch between views of one object and are compact, never giant pills.
 * Filters narrow a list that is already on screen and are lighter again. Both
 * mark their active state with the card surface and blue text rather than an
 * underline or a saturated block.
 */

export interface TabItem {
  id: string;
  label: string;
  count?: number;
}

export interface TabsProps {
  tabs: TabItem[];
  active: string;
  onChange: (id: string) => void;
  label: string;
  className?: string;
  /** `pills` draws the tabs inside a quiet track with the active one lifted on white. */
  variant?: 'default' | 'pills';
}

const TRACK: Record<string, string> = {
  // On a phone the tabs keep one line and scroll sideways rather than wrap.
  default: 'mb-4 flex max-w-full gap-0.5 overflow-x-auto whitespace-nowrap sm:flex-wrap sm:overflow-visible',
  pills: 'mb-5 inline-flex max-w-full gap-0.5 overflow-x-auto whitespace-nowrap rounded-xl bg-canvas-inset p-1 sm:flex-wrap sm:overflow-visible',
};

const TAB_TONE: Record<string, Record<string, string>> = {
  default: {
    true: 'bg-canvas-inset text-ink',
    false: 'text-ink-muted hover:bg-canvas-inset hover:text-ink',
  },
  pills: {
    true: 'bg-white text-ink shadow-sm',
    false: 'text-ink-muted hover:text-ink',
  },
};

const TAB_SHAPE: Record<string, string> = {
  default: 'shrink-0 rounded-lg px-3 py-2 text-[13px]',
  pills: 'shrink-0 rounded-lg px-4 py-2.5 text-[13.5px]',
};

export const Tabs: React.FC<TabsProps> = ({ tabs, active, onChange, label, className = '', variant = 'default' }) => (
  <div role="tablist" aria-label={label} className={`${TRACK[variant]} ${className}`}>
    {tabs.map((t) => {
      const selected = t.id === active;
      const tone = TAB_TONE[variant][String(selected)];
      return (
        <button
          key={t.id}
          type="button"
          role="tab"
          aria-selected={selected}
          onClick={() => onChange(t.id)}
          className={`${TAB_SHAPE[variant]} font-semibold transition-colors ${tone}`}
        >
          {t.label}
          {typeof t.count === 'number' && <span className="ml-1.5 font-sans text-[11px] tabular-nums opacity-70">{t.count}</span>}
        </button>
      );
    })}
  </div>
);

export const FilterChips: React.FC<TabsProps> = ({ tabs, active, onChange, label, className = '' }) => (
  <div role="tablist" aria-label={label} className={`flex flex-wrap gap-1.5 ${className}`}>
    {tabs.map((t) => {
      const selected = t.id === active;
      const tone = {
        true: 'bg-canvas-inset font-semibold text-ink',
        false: 'font-medium text-ink-muted hover:bg-canvas-inset hover:text-ink',
      }[String(selected)];
      return (
        <button
          key={t.id}
          type="button"
          role="tab"
          aria-selected={selected}
          onClick={() => onChange(t.id)}
          className={`rounded-md px-2.5 py-[5px] text-[12px] transition-colors ${tone}`}
        >
          {t.label}
        </button>
      );
    })}
  </div>
);
